import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useUIStore } from '../../stores/uiStore';
import { api } from '../../api/client';
import KeepaSearchBar from './tendencias/KeepaSearchBar';
import TrendingKeywords from './tendencias/TrendingKeywords';
import CategoryTree from './tendencias/CategoryTree';
import CategoryDetailPanel from './tendencias/CategoryDetailPanel';
import AmazonSearchPanel from './tendencias/AmazonSearchPanel';

const SITE_FLAGS: Record<string, string> = { MLA: '\u{1F1E6}\u{1F1F7}', MLM: '\u{1F1F2}\u{1F1FD}', MLC: '\u{1F1E8}\u{1F1F1}', MCO: '\u{1F1E8}\u{1F1F4}' };
const SITE_LABELS: Record<string, string> = { MLA: 'Argentina', MLM: 'Mexico', MLC: 'Chile', MCO: 'Colombia' };
const SITES = ['MLA', 'MLM', 'MLC', 'MCO'];

interface TendenciasSubTabProps {
  sites?: string[];
  filters?: any;
}

export function TendenciasSubTab({ sites, filters }: TendenciasSubTabProps) {
  const { t } = useTranslation();
  const { sellerId } = useUIStore();
  const [siteId, setSiteId] = useState('MLA');
  const [selectedCategory, setSelectedCategory] = useState<any>(null);
  const [amazonSearch, setAmazonSearch] = useState<{ keyword?: string; item_id?: string; title?: string } | null>(null);
  const [trendKeywords, setTrendKeywords] = useState<any[]>([]);

  const fetchTrends = useCallback(async () => {
    try {
      const res = await api.get<any>(
        `/publisher/discover/tendencias/trends?site_id=${siteId}&seller_id=${sellerId}`
      );
      if (res.success && res.data) {
        const arr = Array.isArray(res.data) ? res.data : (res.data as any)?.keywords || [];
        setTrendKeywords(arr.map((k: any) => ({ ...k, type: k.tier || k.type })));
      }
    } catch (err) {
      console.error('Failed to fetch trends', err);
    }
  }, [siteId, sellerId]);

  useEffect(() => {
    fetchTrends();
  }, [fetchTrends]);

  const handleSiteChange = (site: string) => {
    setSiteId(site);
    setSelectedCategory(null);
    setAmazonSearch(null);
    setTrendKeywords([]);
  };

  const trendingKeywordStrings = trendKeywords.map(k => k.keyword).filter(Boolean);

  return (
    <div className="space-y-3">
      {/* Site selector */}
      <div className="flex gap-1">
        {SITES.map(s => (
          <button key={s} onClick={() => handleSiteChange(s)}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
              siteId === s
                ? 'bg-[var(--color-primary)] text-white'
                : 'bg-[var(--color-bg-secondary)] text-[var(--color-text-secondary)] border border-[var(--color-border)] hover:bg-[var(--color-bg-tertiary)]'
            }`}>
            {SITE_FLAGS[s]} {SITE_LABELS[s]}
          </button>
        ))}
      </div>

      {/* Zone 0: Keepa Direct Search */}
      <KeepaSearchBar sellerId={sellerId} trendingKeywords={trendingKeywordStrings} sites={sites} filters={filters} />

      {/* Zone 1: Trending Keywords (shared data) */}
      <TrendingKeywords
        siteId={siteId}
        sellerId={sellerId}
        onKeywordClick={(kw) => setAmazonSearch({ keyword: kw })}
        externalKeywords={trendKeywords.length > 0 ? trendKeywords : undefined}
      />

      {/* Zone 2 + 3 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3" style={{ minHeight: '400px' }}>
        <div className="lg:col-span-1">
          <CategoryTree siteId={siteId} sellerId={sellerId} selectedCategory={selectedCategory} onSelect={setSelectedCategory} />
        </div>
        <div className="lg:col-span-2">
          {selectedCategory ? (
            <CategoryDetailPanel siteId={siteId} sellerId={sellerId} category={selectedCategory}
              onKeywordClick={(kw) => setAmazonSearch({ keyword: kw })}
              onBestsellerSearch={(itemId, title) => setAmazonSearch({ item_id: itemId, title })} />
          ) : (
            <div className="flex items-center justify-center h-full bg-[var(--color-bg-secondary)] rounded-lg border border-[var(--color-border)]">
              <p className="text-sm text-[var(--color-text-tertiary)]">{t('tendencias.selectCategory')}</p>
            </div>
          )}
        </div>
      </div>

      {/* Zone 4: GTIN-first search (from bestsellers/categories) */}
      {amazonSearch && (
        <AmazonSearchPanel sellerId={sellerId} keyword={amazonSearch.keyword} itemId={amazonSearch.item_id}
          title={amazonSearch.title} onClose={() => setAmazonSearch(null)} />
      )}
    </div>
  );
}
